import { LegacyRef } from "react";
// eslint-disable-next-line drops/no-direct-spor-import
import { Button, ButtonProps } from "@vygruppen/spor-react";
import { Semantics } from "shared/components/drops/DropsBadge";
import { ThemeSemantic, useThemeOrSemantic } from "shared/theme/themeSemantic";
import { FCWC } from "shared/utils/FCWC";
import styled, { css } from "styled-components";

type DropsButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "tertiary"
  | "floating";

export interface DropsButtonProps extends Omit<ButtonProps, "variant"> {
  variant?: DropsButtonVariant;
  semantics?: Semantics;
  ref?: LegacyRef<HTMLButtonElement> | undefined;
}

const StyledButton = styled(Button)<{
  themeSemantic: ThemeSemantic;
  semantics: Semantics;
  $dropsVariant: DropsButtonVariant;
}>`
  ${({ $dropsVariant, themeSemantic: theme }) =>
    $dropsVariant === "primary"
      ? css`
          color: ${theme.colorSemanticTextTertiary};
          background-color: ${theme.colorSemantic};
          &:hover {
            background-color: ${theme.colorSemanticPulse};
          }
          &:active {
            background-color: ${theme.colorSemantic};
          }
          &:disabled {
            outline: none;
            opacity: 0.5;
          }
        `
      : css`
          color: ${theme.colorSemanticTextMain};
          background-color: ${theme.colorSemanticFill};
          outline-color: ${theme.colorSemantic};
          &:hover {
            background-color: ${theme.colorSemanticFillPulse};
          }
          &:active {
            background-color: ${theme.colorSemanticFillPulse};
            outline-width: 2px;
          }
          &:disabled {
            outline: none;
            opacity: 0.5;
          }
        `}
`;

// Tips: If you are using a button inside button, use as="div" to prevent invalidDOM
export const InternalDropsButton: FCWC<DropsButtonProps> = ({
  ref = null,
  variant = "primary",
  semantics,
  children,
  ...props
}) => {
  const theme = useThemeOrSemantic(semantics);
  if (!semantics) {
    switch (variant) {
      case "primary":
        return (
          <Button ref={ref} variant="primary" {...props}>
            {children}
          </Button>
        );
      case "secondary":
        return (
          <Button ref={ref} variant="secondary" {...props}>
            {children}
          </Button>
        );
      case "ghost":
        return (
          <Button ref={ref} variant="ghost" {...props}>
            {children}
          </Button>
        );
      case "tertiary":
        return (
          <Button ref={ref} variant="tertiary" {...props}>
            {children}
          </Button>
        );
      case "floating":
        return (
          <Button ref={ref} variant="floating" {...props}>
            {children}
          </Button>
        );
      case undefined:
        return (
          <Button ref={ref} {...props}>
            {children}
          </Button>
        );
    }
  }
  return (
    <StyledButton
      ref={ref}
      variant={variant}
      {...props}
      themeSemantic={theme}
      semantics={semantics}
      $dropsVariant={variant ?? "primary"}
    >
      {children}
    </StyledButton>
  );
};
